import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { Capacitor } from '@capacitor/core';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthenticationDTO } from '../entities/dto/authentication.dto';
import { UserDTO } from '../entities/dto/user.dto';
import { FirebaseCloudMessagingService } from '../notification-alerte/infra/firebase-cloud-messaging.service';
import { CrashlyticsService } from '../shared/crashlytics.service';
import { LoggingService } from '../shared/logging.service';
import { DeviceService } from './device.service';
import { ProfileService } from './profile.service';
import { ServerUrl } from './server-url.service';
import { TokenService } from './token.service';

type EntityResponseType = HttpResponse<AuthenticationDTO>;

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly #http = inject(HttpClient);
  readonly #tokenService = inject(TokenService);
  readonly #serverUrl = inject(ServerUrl);
  readonly #device = inject(DeviceService);
  readonly #fcm = inject(FirebaseCloudMessagingService);
  readonly #profileService = inject(ProfileService);
  readonly #log = inject(LoggingService);
  readonly #crashlytics = inject(CrashlyticsService);

  public static readonly ROLE_AGENT = 'ROLE_AGENT';
  public static readonly ROLE_RESPONSABLE = 'ROLE_RESPONSABLE';
  private user: UserDTO | undefined;
  private userChange: Subject<UserDTO | undefined> = new Subject<UserDTO | undefined>();

  public async authenticate(ref: string): Promise<UserDTO> {
    const device = await this.#device.fetch();
    const payload = { username: ref, password: ref, device, rememberMe: true };
    return lastValueFrom(
      this.#http.post<AuthenticationDTO>(this.#serverUrl.get() + '/../api/authenticate', payload, { observe: 'response' }).pipe(
        map((res: EntityResponseType) => res.body as AuthenticationDTO),
        tap((res: AuthenticationDTO) => this.#tokenService.storeToken(res.id_token as string)),
        map((res: AuthenticationDTO) => UserDTO.hydrate(res.user as UserDTO)),
        tap((user: UserDTO) => (this.user = user)), // store locally
        tap((user: UserDTO) => this.userChange.next(user)),
        tap((user: UserDTO) => this.#tokenService.storeUser(user)), // store permanently
        tap((user: UserDTO) => this.registerCrashlytics(user))
      )
    );
  }

  public async logout() {
    if (Capacitor.isNativePlatform()) {
      try {
        await lastValueFrom(this.#fcm.destroy(this.user?.authorities.includes(AuthService.ROLE_RESPONSABLE) ?? false));
      } catch (e) {
        this.#log.warn('Failed to destroy fcm token');
      }
    }
    this.#tokenService.clearToken();
    this.#crashlytics.unsetUserId();
    this.#tokenService.clearUser();
    this.#profileService.clear();
    this.#serverUrl.clear();
    this.user = undefined;
    this.userChange.next(this.user);
  }

  public async getUser(): Promise<UserDTO | undefined> {
    if (!this.user) {
      this.user = this.#tokenService.getUser();
      if (!this.user && this.#tokenService.getToken()) {
        this.user = await this.loadUser();
      }
      this.userChange.next(this.user);
    }
    return this.user;
  }

  public async loadUser(): Promise<UserDTO> {
    return lastValueFrom(
      this.#http.get<UserDTO>(this.#serverUrl.get() + '/../api/account').pipe(
        map(res => UserDTO.hydrate(res)),
        tap((res: UserDTO) => this.#tokenService.storeUser(res)),
        tap((user: UserDTO) => {
          this.user = user;
          this.userChange.next(this.user);
        })
      )
    );
  }

  public onUserChange(): Observable<UserDTO | undefined> {
    return this.userChange.asObservable();
  }

  private async registerCrashlytics(user: UserDTO): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await FirebaseCrashlytics.setUserId({
        userId: user?.id,
      });
    }
  }
}
